"use client";

import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useAction } from "convex/react";
import { useTranslations } from "next-intl";
import * as Sentry from "@sentry/nextjs";
import { Link } from "@/i18n/navigation";
import posthog from "posthog-js";
import { api } from "../../../convex/_generated/api";
import { AuthCard } from "./auth-card";
import { AuthInput } from "./auth-input";
import { AuthButton } from "./auth-button";
import { OAuthButtons } from "./oauth-buttons";
import { OrDivider } from "./or-divider";
import { LegalFooter } from "./legal-footer";
import { EmailInUseNotice } from "./email-in-use-notice";
import { errorCode } from "./error-mapping";
import { setSessionCookie } from "./session-helpers";

export function SignInForm() {
  const t = useTranslations("auth");
  const loginWithPassword = useAction(api.emailAuth.loginWithPassword);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const noticeRef = useRef<HTMLDivElement | null>(null);

  const emailInUseProvider =
    errorKey === "errors.emailInUseGoogle"
      ? "google"
      : errorKey === "errors.emailInUseApple"
        ? "apple"
        : null;

  useEffect(() => {
    if (emailInUseProvider && noticeRef.current) {
      noticeRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [emailInUseProvider]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErrorKey(null);
    try {
      // Race loginWithPassword against a 20s timeout so a stuck Convex socket
      // surfaces as an error instead of a permanent spinner.
      const result = (await Promise.race([
        loginWithPassword({ email: email.trim(), password }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("loginWithPassword timeout")), 20_000),
        ),
      ])) as { sessionToken: string };
      setSessionCookie(result.sessionToken);
      posthog.capture("signed_in", { auth_provider: "email" });
      window.location.href = "/new";
    } catch (err) {
      const code = errorCode(err);
      setErrorKey(code);
      posthog.capture("sign_in_failed", { auth_provider: "email", error_code: code });
      if (code === "errors.generic") {
        Sentry.captureException(err, { tags: { feature: "email_signin" } });
      }
      setLoading(false);
    }
  }

  return (
    <AuthCard
      title={t("signIn.title")}
      subtitle={
        <>
          {t("signIn.subtitleBefore")}{" "}
          <Link href="/sign-up" className="font-medium text-ink underline underline-offset-4 decoration-ink/30 hover:decoration-ink">
            {t("signIn.subtitleLink")}
          </Link>
          .
        </>
      }
      footer={<LegalFooter />}
    >
      {emailInUseProvider && (
        <div ref={noticeRef}>
          <EmailInUseNotice provider={emailInUseProvider} email={email.trim().toLowerCase()} />
        </div>
      )}

      {/* OAuth visible by default (NEXT_PUBLIC_OAUTH_ENABLED=false to hide). Endpoints stay live for existing sessions. */}
      {process.env.NEXT_PUBLIC_OAUTH_ENABLED !== "false" && (
        <>
          <OAuthButtons source="sign_in" />
          <OrDivider />
        </>
      )}

      {process.env.NEXT_PUBLIC_OAUTH_ENABLED === "false" && (
        <p className="rounded-xl border border-line bg-surface-mute px-3 py-2.5 text-[13px] leading-snug text-ink-mute">
          {t("signIn.oauthMigrationHint")}
        </p>
      )}

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <AuthInput
          label={t("signIn.emailLabel")}
          name="email"
          type="email"
          autoComplete="email"
          required
          placeholder={t("signIn.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div className="flex flex-col gap-1.5">
          <AuthInput
            label={t("signIn.passwordLabel")}
            name="password"
            type="password"
            autoComplete="current-password"
            required
            placeholder={t("signIn.passwordPlaceholder")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Link
            href="/forgot-password"
            className="self-end text-[13px] font-medium text-ink-mute underline underline-offset-4 decoration-ink/20 hover:text-ink hover:decoration-ink"
          >
            {t("signIn.forgot")}
          </Link>
        </div>
        {errorKey && !emailInUseProvider && (
          <p className="text-sm text-red-400 text-center">{t(errorKey)}</p>
        )}
        <AuthButton type="submit" loading={loading} className="mt-2">
          {t("signIn.submit")}
        </AuthButton>
      </form>
    </AuthCard>
  );
}
